import {
  CADENCES,
  HABIT_DEFAULT_GROUP,
  type HabitDueKey,
  type HabitFilter,
  type HabitGroupKey,
  type HabitSortKey,
  cadenceLabelKey,
} from '@goaltree/backend/convex/lib/habitView'
import {
  TASK_DEFAULT_GROUP,
  type TaskDueKey,
  type TaskFilter,
  type TaskGroupKey,
  type TaskPriority,
  type TaskRepeatKey,
  type TaskSortKey,
} from '@goaltree/backend/convex/lib/taskView'
import {
  Popover,
  PopoverClose,
  PopoverContent,
  PopoverTrigger,
} from '@goaltree/ui/components/popover'
import { cn } from '@goaltree/ui/lib/utils'
import { Check, ChevronDown, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { CATEGORY_COLORS, useCategoryFill } from '@/lib/category-color'

const CATEGORIES = ['health', 'career', 'mind', 'finance', 'relationships', 'creative'] as const

// ─── Goals ────────────────────────────────────────────────────────────────────

export type GoalSortKey = 'manual' | 'progress' | 'remaining' | 'title'
export type GoalGroupKey = 'none' | 'category' | 'status'

export type GoalTypeKey = 'all' | 'starred' | 'recent'

interface GoalsFilterBarProps {
  categories: string[]
  sort: GoalSortKey
  group: GoalGroupKey
  type: GoalTypeKey
  hideCompleted?: boolean
  onCategories: (c: string[]) => void
  onSort: (s: GoalSortKey) => void
  onGroup: (g: GoalGroupKey) => void
  onType: (v: GoalTypeKey) => void
  onHideCompleted?: (v: boolean) => void
  onReset: () => void
  showGroup?: boolean
  rightSlot?: React.ReactNode
}

export function GoalsFilterBar({
  categories,
  sort,
  group,
  type,
  hideCompleted = false,
  onCategories,
  onSort,
  onGroup,
  onType,
  onHideCompleted,
  onReset,
  showGroup = true,
  rightSlot,
}: GoalsFilterBarProps) {
  const { t } = useTranslation()
  const fill = useCategoryFill()

  const GOAL_SORT_OPTIONS: { value: GoalSortKey; label: string }[] = [
    { value: 'manual', label: t('filter.sortManual') },
    { value: 'progress', label: t('filter.sortProgressHigh') },
    { value: 'remaining', label: t('filter.sortRemainingTasks') },
    { value: 'title', label: t('filter.sortTitleAZ') },
  ]

  const GOAL_GROUP_OPTIONS: { value: GoalGroupKey; label: string }[] = [
    { value: 'none', label: t('filter.noGrouping') },
    { value: 'category', label: t('filter.groupCategory') },
    { value: 'status', label: t('filter.groupStatus') },
  ]

  const TYPE_OPTIONS: { value: GoalTypeKey; label: string }[] = [
    { value: 'all', label: t('filter.typeAll') },
    { value: 'recent', label: t('nav.recents') },
    { value: 'starred', label: t('filter.starred') },
  ]

  const filterActiveCount = categories.length + (type !== 'all' ? 1 : 0) + (hideCompleted ? 1 : 0)
  const activeCount = filterActiveCount + (sort !== 'manual' ? 1 : 0) + (group !== 'none' ? 1 : 0)
  const sortLabel = GOAL_SORT_OPTIONS.find((o) => o.value === sort)?.label ?? t('filter.sort')
  const groupLabel = GOAL_GROUP_OPTIONS.find((o) => o.value === group)?.label ?? t('filter.group')

  function toggleCategory(cat: string) {
    onCategories(
      categories.includes(cat) ? categories.filter((c) => c !== cat) : [...categories, cat],
    )
  }

  return (
    <FilterBar activeCount={activeCount} onReset={onReset} rightSlot={rightSlot}>
      <OptionsPopover
        label={sort !== 'manual' ? sortLabel : t('filter.sort')}
        active={sort !== 'manual'}
      >
        <SectionLabel>{t('filter.sortBy')}</SectionLabel>
        {GOAL_SORT_OPTIONS.map((opt) => (
          <PopoverClose
            key={opt.value}
            onClick={() => onSort(opt.value)}
            className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-xs transition-colors hover:bg-muted cursor-pointer"
          >
            <Check
              className={cn('h-3 w-3 shrink-0', sort === opt.value ? 'opacity-100' : 'opacity-0')}
            />
            {opt.label}
          </PopoverClose>
        ))}
      </OptionsPopover>

      {showGroup && (
        <OptionsPopover
          label={
            group !== 'none' ? t('filter.groupActive', { label: groupLabel }) : t('filter.group')
          }
          active={group !== 'none'}
          width="w-44"
        >
          <SectionLabel>{t('filter.groupBy')}</SectionLabel>
          {GOAL_GROUP_OPTIONS.map((opt) => (
            <PopoverClose
              key={opt.value}
              onClick={() => onGroup(opt.value)}
              className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-xs transition-colors hover:bg-muted cursor-pointer"
            >
              <Check
                className={cn(
                  'h-3 w-3 shrink-0',
                  group === opt.value ? 'opacity-100' : 'opacity-0',
                )}
              />
              {opt.label}
            </PopoverClose>
          ))}
        </OptionsPopover>
      )}

      <OptionsPopover
        label={
          filterActiveCount > 0
            ? t('filter.filterActive', { count: filterActiveCount })
            : t('filter.filterLabel')
        }
        active={filterActiveCount > 0}
        width="w-52"
      >
        <SectionLabel>{t('filter.category')}</SectionLabel>
        {CATEGORIES.map((cat) => (
          <button
            key={cat}
            type="button"
            onClick={() => toggleCategory(cat)}
            className="cursor-pointer flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-xs transition-colors hover:bg-muted"
          >
            <span
              className="h-2 w-2 shrink-0 rounded-full"
              style={{ backgroundColor: fill(CATEGORY_COLORS[cat]) }}
            />
            <Checkbox checked={categories.includes(cat)} />
            {t(`goals.cat.${cat}`)}
          </button>
        ))}
        <SectionLabel className="mt-2">{t('filter.type')}</SectionLabel>
        {TYPE_OPTIONS.map((opt) => (
          <button
            key={opt.value}
            type="button"
            onClick={() => onType(opt.value)}
            className="cursor-pointer flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-xs transition-colors hover:bg-muted"
          >
            <Checkbox checked={type === opt.value} />
            {opt.label}
          </button>
        ))}
        {onHideCompleted && (
          <>
            <SectionLabel className="mt-2">{t('filter.completed')}</SectionLabel>
            <button
              type="button"
              onClick={() => onHideCompleted(!hideCompleted)}
              className="cursor-pointer flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-xs transition-colors hover:bg-muted"
            >
              <Checkbox checked={hideCompleted} />
              {t('goals.hideCompleted')}
            </button>
          </>
        )}
      </OptionsPopover>

      <ActiveChips
        items={categories.map((c) => ({
          key: c,
          label: t(`goals.cat.${c}`),
          color: fill(CATEGORY_COLORS[c]),
        }))}
        onRemove={(k) => onCategories(categories.filter((c) => c !== k))}
      />
    </FilterBar>
  )
}

// ─── Tasks ─────────────────────────────────────────────────────────────────────

interface TasksFilterBarProps {
  filter: TaskFilter
  allTags: string[]
  onChange: (f: Partial<TaskFilter>) => void
  onReset: () => void
  showGroup?: boolean
  rightSlot?: React.ReactNode
  showNumbers?: boolean
  onShowNumbers?: (v: boolean) => void
}

export function TasksFilterBar({
  filter,
  allTags,
  onChange,
  onReset,
  showGroup = true,
  rightSlot,
  showNumbers = false,
  onShowNumbers,
}: TasksFilterBarProps) {
  const { t } = useTranslation()
  const { sort, group, due, priorities, doToday, statuses, tags, repeat } = filter

  const SORTS: { value: TaskSortKey; label: string }[] = [
    { value: 'default', label: t('filter.sortDefault') },
    { value: 'due', label: t('filter.sortDue') },
    { value: 'priority', label: t('filter.sortPriority') },
    { value: 'created', label: t('filter.groupCreated') },
    { value: 'title', label: t('filter.sortTitleAZ') },
  ]
  const GROUPS: { value: TaskGroupKey; label: string }[] = [
    { value: 'created', label: t('filter.groupCreated') },
    { value: 'due', label: t('filter.groupDue') },
    { value: 'priority', label: t('filter.groupPriority') },
    { value: 'tag', label: t('filter.groupTag') },
    { value: 'none', label: t('filter.noGrouping') },
  ]
  const DUES: { value: TaskDueKey; label: string }[] = [
    { value: 'overdue', label: t('filter.due.overdue') },
    { value: 'today', label: t('filter.due.today') },
    { value: 'week', label: t('filter.due.week') },
    { value: 'none', label: t('filter.due.none') },
  ]
  const PRIOS: { value: TaskPriority; label: string }[] = [
    { value: 'super', label: t('filter.prioritySuperUrgent') },
    { value: 'urgent', label: t('filter.priorityUrgent') },
    { value: 'normal', label: t('filter.priorityNormal') },
  ]
  const REPEATS: { value: TaskRepeatKey; label: string }[] = [
    { value: 'repeating', label: t('filter.repeating') },
    { value: 'oneTime', label: t('inbox.oneTimeLabel') },
  ]
  const STATUSES = [
    { value: 'pending', label: t('filter.statusPending') },
    { value: 'in-progress', label: t('goals.inProgress') },
    { value: 'done', label: t('filter.statusDone') },
  ]
  const toggle = <T extends string>(list: T[], v: T) =>
    list.includes(v) ? list.filter((x) => x !== v) : [...list, v]

  const filterActiveCount =
    due.length +
    priorities.length +
    (doToday ? 1 : 0) +
    statuses.length +
    tags.length +
    repeat.length
  const activeCount =
    filterActiveCount + (sort !== 'default' ? 1 : 0) + (group !== TASK_DEFAULT_GROUP ? 1 : 0)
  const groupLabel = GROUPS.find((o) => o.value === group)?.label ?? t('filter.group')
  const item =
    'cursor-pointer flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-xs transition-colors hover:bg-muted'

  return (
    <FilterBar activeCount={activeCount} onReset={onReset} rightSlot={rightSlot}>
      <OptionsPopover
        label={sort !== 'default' ? SORTS.find((o) => o.value === sort)!.label : t('filter.sort')}
        active={sort !== 'default'}
      >
        <SectionLabel>{t('filter.sortBy')}</SectionLabel>
        {SORTS.map((opt) => (
          <PopoverClose
            key={opt.value}
            onClick={() => onChange({ sort: opt.value })}
            className={item}
          >
            <Check
              className={cn('h-3 w-3 shrink-0', sort === opt.value ? 'opacity-100' : 'opacity-0')}
            />
            {opt.label}
          </PopoverClose>
        ))}
        {onShowNumbers && (
          <>
            <SectionLabel className="mt-2">{t('filter.view')}</SectionLabel>
            <button type="button" onClick={() => onShowNumbers(!showNumbers)} className={item}>
              <Checkbox checked={showNumbers} />
              {t('filter.showNumbers')}
            </button>
          </>
        )}
      </OptionsPopover>

      {showGroup && (
        <OptionsPopover
          label={
            group !== 'none' ? t('filter.groupActive', { label: groupLabel }) : t('filter.group')
          }
          active={group !== TASK_DEFAULT_GROUP}
          width="w-44"
        >
          <SectionLabel>{t('filter.groupBy')}</SectionLabel>
          {GROUPS.map((opt) => (
            <PopoverClose
              key={opt.value}
              onClick={() => onChange({ group: opt.value })}
              className={item}
            >
              <Check
                className={cn(
                  'h-3 w-3 shrink-0',
                  group === opt.value ? 'opacity-100' : 'opacity-0',
                )}
              />
              {opt.label}
            </PopoverClose>
          ))}
        </OptionsPopover>
      )}

      <OptionsPopover
        label={
          filterActiveCount > 0
            ? t('filter.filterActive', { count: filterActiveCount })
            : t('filter.filterLabel')
        }
        active={filterActiveCount > 0}
        width="w-52"
      >
        <SectionLabel>{t('filter.dueLabel')}</SectionLabel>
        {DUES.map((opt) => (
          <button
            key={opt.value}
            type="button"
            onClick={() => onChange({ due: toggle(due, opt.value) })}
            className={item}
          >
            <Checkbox checked={due.includes(opt.value)} />
            {opt.label}
          </button>
        ))}
        <SectionLabel className="mt-2">{t('filter.groupPriority')}</SectionLabel>
        {PRIOS.map((opt) => (
          <button
            key={opt.value}
            type="button"
            onClick={() => onChange({ priorities: toggle(priorities, opt.value) })}
            className={item}
          >
            <Checkbox checked={priorities.includes(opt.value)} />
            {opt.label}
          </button>
        ))}
        <button
          type="button"
          onClick={() => onChange({ doToday: !doToday })}
          className={cn(item, 'mt-2')}
        >
          <Checkbox checked={doToday} />
          {t('mustDo.short')}
        </button>
        <SectionLabel className="mt-2">{t('filter.status')}</SectionLabel>
        {STATUSES.map((opt) => (
          <button
            key={opt.value}
            type="button"
            onClick={() => onChange({ statuses: toggle(statuses, opt.value) })}
            className={item}
          >
            <Checkbox checked={statuses.includes(opt.value)} />
            {opt.label}
          </button>
        ))}
        <SectionLabel className="mt-2">{t('filter.repeats')}</SectionLabel>
        {REPEATS.map((opt) => (
          <button
            key={opt.value}
            type="button"
            onClick={() => onChange({ repeat: toggle(repeat, opt.value) })}
            className={item}
          >
            <Checkbox checked={repeat.includes(opt.value)} />
            {opt.label}
          </button>
        ))}
        {allTags.length > 0 && (
          <>
            <SectionLabel className="mt-2">{t('filter.groupTag')}</SectionLabel>
            {allTags.map((tag) => (
              <button
                key={tag}
                type="button"
                onClick={() => onChange({ tags: toggle(tags, tag) })}
                className={item}
              >
                <Checkbox checked={tags.includes(tag)} />#{tag}
              </button>
            ))}
          </>
        )}
      </OptionsPopover>

      <ActiveChips
        items={[
          ...due.map((d) => ({ key: `due:${d}`, label: DUES.find((o) => o.value === d)!.label })),
          ...priorities.map((p) => ({
            key: `pri:${p}`,
            label: PRIOS.find((o) => o.value === p)!.label,
          })),
          ...(doToday ? [{ key: 'do:', label: t('mustDo.short') }] : []),
          ...statuses.map((s) => ({
            key: `sta:${s}`,
            label: STATUSES.find((o) => o.value === s)?.label ?? s,
          })),
          ...repeat.map((r) => ({
            key: `rep:${r}`,
            label: REPEATS.find((o) => o.value === r)!.label,
          })),
          ...tags.map((tag) => ({ key: `tag:${tag}`, label: `#${tag}` })),
        ]}
        onRemove={(k) => {
          const v = k.slice(k.indexOf(':') + 1)
          if (k.startsWith('due:')) onChange({ due: due.filter((x) => x !== v) })
          else if (k.startsWith('pri:')) onChange({ priorities: priorities.filter((x) => x !== v) })
          else if (k.startsWith('do:')) onChange({ doToday: false })
          else if (k.startsWith('sta:')) onChange({ statuses: statuses.filter((x) => x !== v) })
          else if (k.startsWith('rep:')) onChange({ repeat: repeat.filter((x) => x !== v) })
          else onChange({ tags: tags.filter((x) => x !== v) })
        }}
      />
    </FilterBar>
  )
}

// ─── Habits ────────────────────────────────────────────────────────────────────

interface HabitsFilterBarProps {
  filter: HabitFilter
  allTags: string[]
  onChange: (f: Partial<HabitFilter>) => void
  onReset: () => void
  rightSlot?: React.ReactNode
}

export function HabitsFilterBar({
  filter,
  allTags,
  onChange,
  onReset,
  rightSlot,
}: HabitsFilterBarProps) {
  const { t } = useTranslation()
  const fill = useCategoryFill()
  const { sort, group, due, categories, cadences, tags } = filter

  const SORTS: { value: HabitSortKey; label: string }[] = [
    { value: 'default', label: t('filter.sortDefault') },
    { value: 'streak', label: t('filter.sortStreak') },
    { value: 'strength', label: t('filter.sortStrength') },
    { value: 'time', label: t('filter.sortTime') },
    { value: 'title', label: t('filter.sortTitleAZ') },
  ]
  const GROUPS: { value: HabitGroupKey; label: string }[] = [
    { value: 'none', label: t('filter.noGrouping') },
    { value: 'time', label: t('filter.groupTime') },
    { value: 'category', label: t('filter.groupCategory') },
    { value: 'cadence', label: t('filter.groupCadence') },
    { value: 'tag', label: t('filter.groupTag') },
  ]
  const DUES: { value: HabitDueKey; label: string }[] = [
    { value: 'due', label: t('filter.dueToday') },
    { value: 'done', label: t('filter.doneToday') },
    { value: 'notDue', label: t('filter.notDueToday') },
  ]
  const CATS = [...CATEGORIES, ''] as string[]
  const catLabel = (c: string) => (c ? t(`goals.cat.${c}`) : t('filter.noCategory'))
  const toggle = <T extends string>(list: T[], v: T) =>
    list.includes(v) ? list.filter((x) => x !== v) : [...list, v]

  const filterActiveCount = due.length + categories.length + cadences.length + tags.length
  const activeCount =
    filterActiveCount + (sort !== 'default' ? 1 : 0) + (group !== HABIT_DEFAULT_GROUP ? 1 : 0)
  const groupLabel = GROUPS.find((o) => o.value === group)?.label ?? t('filter.group')
  const item =
    'cursor-pointer flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-xs transition-colors hover:bg-muted'

  return (
    <FilterBar activeCount={activeCount} onReset={onReset} rightSlot={rightSlot}>
      <OptionsPopover
        label={sort !== 'default' ? SORTS.find((o) => o.value === sort)!.label : t('filter.sort')}
        active={sort !== 'default'}
      >
        <SectionLabel>{t('filter.sortBy')}</SectionLabel>
        {SORTS.map((opt) => (
          <PopoverClose
            key={opt.value}
            onClick={() => onChange({ sort: opt.value })}
            className={item}
          >
            <Check
              className={cn('h-3 w-3 shrink-0', sort === opt.value ? 'opacity-100' : 'opacity-0')}
            />
            {opt.label}
          </PopoverClose>
        ))}
      </OptionsPopover>

      <OptionsPopover
        label={
          group !== 'none' ? t('filter.groupActive', { label: groupLabel }) : t('filter.group')
        }
        active={group !== HABIT_DEFAULT_GROUP}
        width="w-44"
      >
        <SectionLabel>{t('filter.groupBy')}</SectionLabel>
        {GROUPS.map((opt) => (
          <PopoverClose
            key={opt.value}
            onClick={() => onChange({ group: opt.value })}
            className={item}
          >
            <Check
              className={cn('h-3 w-3 shrink-0', group === opt.value ? 'opacity-100' : 'opacity-0')}
            />
            {opt.label}
          </PopoverClose>
        ))}
      </OptionsPopover>

      <OptionsPopover
        label={
          filterActiveCount > 0
            ? t('filter.filterActive', { count: filterActiveCount })
            : t('filter.filterLabel')
        }
        active={filterActiveCount > 0}
        width="w-52"
      >
        <SectionLabel>{t('filter.today')}</SectionLabel>
        {DUES.map((opt) => (
          <button
            key={opt.value}
            type="button"
            onClick={() => onChange({ due: toggle(due, opt.value) })}
            className={item}
          >
            <Checkbox checked={due.includes(opt.value)} />
            {opt.label}
          </button>
        ))}
        <SectionLabel className="mt-2">{t('filter.category')}</SectionLabel>
        {CATS.map((cat) => (
          <button
            key={cat}
            type="button"
            onClick={() => onChange({ categories: toggle(categories, cat) })}
            className={item}
          >
            {cat ? (
              <span
                className="h-2 w-2 shrink-0 rounded-full"
                style={{
                  backgroundColor: fill(CATEGORY_COLORS[cat as (typeof CATEGORIES)[number]]),
                }}
              />
            ) : (
              <span className="h-2 w-2 shrink-0" />
            )}
            <Checkbox checked={categories.includes(cat)} />
            {catLabel(cat)}
          </button>
        ))}
        <SectionLabel className="mt-2">{t('filter.cadence')}</SectionLabel>
        {CADENCES.map((c) => (
          <button
            key={c}
            type="button"
            onClick={() => onChange({ cadences: toggle(cadences, c) })}
            className={item}
          >
            <Checkbox checked={cadences.includes(c)} />
            {t(cadenceLabelKey(c))}
          </button>
        ))}
        {allTags.length > 0 && (
          <>
            <SectionLabel className="mt-2">{t('filter.groupTag')}</SectionLabel>
            {allTags.map((tag) => (
              <button
                key={tag}
                type="button"
                onClick={() => onChange({ tags: toggle(tags, tag) })}
                className={item}
              >
                <Checkbox checked={tags.includes(tag)} />#{tag}
              </button>
            ))}
          </>
        )}
      </OptionsPopover>

      <ActiveChips
        items={[
          ...due.map((d) => ({ key: `due:${d}`, label: DUES.find((o) => o.value === d)!.label })),
          ...categories.map((c) => ({
            key: `cat:${c}`,
            label: catLabel(c),
            color: c ? fill(CATEGORY_COLORS[c as (typeof CATEGORIES)[number]]) : undefined,
          })),
          ...cadences.map((c) => ({
            key: `cad:${c}`,
            label: t(cadenceLabelKey(c as (typeof CADENCES)[number])),
          })),
          ...tags.map((tag) => ({ key: `tag:${tag}`, label: `#${tag}` })),
        ]}
        onRemove={(k) => {
          const v = k.slice(k.indexOf(':') + 1)
          if (k.startsWith('due:')) onChange({ due: due.filter((x) => x !== v) })
          else if (k.startsWith('cat:')) onChange({ categories: categories.filter((x) => x !== v) })
          else if (k.startsWith('cad:')) onChange({ cadences: cadences.filter((x) => x !== v) })
          else onChange({ tags: tags.filter((x) => x !== v) })
        }}
      />
    </FilterBar>
  )
}

// ─── Shared primitives ─────────────────────────────────────────────────────────

function FilterBar({
  activeCount,
  onReset,
  children,
  rightSlot,
}: {
  activeCount: number
  onReset: () => void
  children: React.ReactNode
  rightSlot?: React.ReactNode
}) {
  const { t } = useTranslation()
  return (
    // Controls scroll sideways on one line so active labels ("Group: Time of day") never
    // push the right-hand toggle onto a second row.
    <div className="flex items-center gap-1.5">
      <div className="flex min-w-0 flex-1 items-center gap-1.5 overflow-x-auto whitespace-nowrap [scrollbar-width:none] [&>*]:shrink-0 [&::-webkit-scrollbar]:hidden">
        {children}
        {activeCount > 0 && (
          <button
            type="button"
            onClick={onReset}
            className="cursor-pointer flex h-7 items-center gap-1 rounded-md px-2 text-xs text-muted-foreground/70 transition-colors hover:text-foreground"
            title={t('filter.clearTitle')}
          >
            <X className="h-3 w-3" />
            {t('filter.clear')}
          </button>
        )}
      </div>
      {rightSlot && <div className="flex shrink-0 items-center">{rightSlot}</div>}
    </div>
  )
}

function OptionsPopover({
  label,
  active,
  width = 'w-48',
  children,
}: {
  label: string
  active: boolean
  width?: string
  children: React.ReactNode
}) {
  return (
    <Popover>
      <PopoverTrigger
        className={cn(
          'cursor-pointer flex h-7 items-center gap-1 rounded-md px-2 text-xs font-medium transition-colors',
          active
            ? 'bg-primary/10 text-primary-text ring-1 ring-primary/30'
            : 'bg-muted/40 text-muted-foreground hover:bg-muted hover:text-foreground',
        )}
      >
        {label}
        <ChevronDown className="h-3 w-3 opacity-60" />
      </PopoverTrigger>
      <PopoverContent align="start" className={width}>
        {children}
      </PopoverContent>
    </Popover>
  )
}

// oxlint-disable-next-line no-unused-vars
function SectionLabel({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <p
      className={cn(
        'mb-1.5 px-1 text-[10px] font-bold uppercase tracking-widest text-muted-foreground',
        className,
      )}
    >
      {children}
    </p>
  )
}

function Checkbox({ checked }: { checked: boolean }) {
  return (
    <div
      className={cn(
        'flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded border',
        checked ? 'border-primary bg-primary' : 'border-border',
      )}
    >
      {checked && <Check className="h-2.5 w-2.5 text-[#0a0a0a]" />}
    </div>
  )
}

function ActiveChips({
  items,
  onRemove,
}: {
  items: { key: string; label: string; color?: string }[]
  onRemove: (key: string) => void
}) {
  if (items.length === 0) return null
  return (
    <>
      {items.map((item) => (
        <span
          key={item.key}
          className="flex h-6 items-center gap-1 rounded-full border border-border bg-muted/50 pl-2 pr-1 text-[11px] font-medium uppercase tracking-wide text-foreground"
        >
          {item.label}
          <button
            type="button"
            onClick={() => onRemove(item.key)}
            className="ml-0.5 rounded-full p-0.5 text-muted-foreground hover:text-foreground transition-colors"
          >
            <X className="h-2.5 w-2.5" />
          </button>
        </span>
      ))}
    </>
  )
}
